import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SupplierLayout } from '../../components/SupplierLayout';
import { useSupplierStore } from '../../lib/supplierStore';
import { ShieldCheck, AlertTriangle, WifiOff, Wifi, Search, Truck as TruckIcon, Calendar, Cpu, CreditCard, CheckCircle2, Clock, XCircle, ChevronRight, Download, X, Sparkles, FileText, Lock } from 'lucide-react';
import { toast } from 'sonner';
// --- Deterministic per-truck demo data ----------------------------------
// Self-contained: derives permit + IoT data from truck.id so the page
// renders the same way every refresh without touching the supplier store.
const PERMIT_VALIDITY_DAYS: Record<string, number> = {
  // days remaining from "today" — positive = valid, negative = expired
  t1: 142,
  t2: 18,
  t3: 87,
  t4: -7,
  t5: 230,
  t6: 6,
  t7: 65,
  t8: -32 // expired
};
const GPS_OFFLINE: Record<string, true | undefined> = {
  t3: true,
  t4: true,
  t8: true
};
const GPS_LAST_PING: Record<string, string> = {
  t3: '4 hours ago',
  t4: '2 days ago',
  t8: '12 hours ago'
};
function permitNumberFor(truckId: string, plate: string) {
  const tail = (truckId.replace(/\D/g, '') || '0').padStart(4, '0');
  const plateClean = plate.replace(/\s/g, '').toUpperCase();
  return `RTA-VPM-${plateClean}-${tail}`;
}
function validUntilFor(truckId: string) {
  const days = PERMIT_VALIDITY_DAYS[truckId] ?? 90;
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d;
}
function daysFromNow(d: Date) {
  const diff = d.getTime() - Date.now();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}
function formatDate(d: Date) {
  return d.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
}
type Filter = 'all' | 'expiring' | 'expired' | 'offline' | 'healthy';
export function SupplierPermits() {
  const {
    trucks,
    drivers
  } = useSupplierStore();
  const [filter, setFilter] = useState<Filter>('all');
  const [search, setSearch] = useState('');
  const [payingTruckId, setPayingTruckId] = useState<string | null>(null);
  // Enrich each truck with derived permit + IoT data
  const enriched = useMemo(() => {
    return trucks.map(t => {
      const validUntil = validUntilFor(t.id);
      const days = daysFromNow(validUntil);
      const expired = days < 0;
      const expiringSoon = !expired && days <= 30;
      const offline = !!GPS_OFFLINE[t.id];
      const driver = drivers.find(d => d.id === t.assignedDriverId);
      const flagged = expired || expiringSoon || offline;
      return {
        ...t,
        permit: {
          number: permitNumberFor(t.id, t.plate),
          validUntil,
          days,
          expired,
          expiringSoon
        },
        iot: {
          online: !offline,
          lastPing: offline ? GPS_LAST_PING[t.id] || 'Unknown' : 'Just now'
        },
        driverName: driver?.name,
        flagged
      };
    });
  }, [trucks, drivers]);
  const filtered = useMemo(() => {
    return enriched.filter(t => {
      if (search) {
        const q = search.toLowerCase();
        if (!t.plate.toLowerCase().includes(q) && !t.type.toLowerCase().includes(q) && !t.permit.number.toLowerCase().includes(q)) {
          return false;
        }
      }
      if (filter === 'all') return true;
      if (filter === 'expiring') return t.permit.expiringSoon;
      if (filter === 'expired') return t.permit.expired;
      if (filter === 'offline') return !t.iot.online;
      if (filter === 'healthy') return !t.permit.expired && !t.permit.expiringSoon && t.iot.online;
      return true;
    })
    // Sort: most urgent first
    .sort((a, b) => {
      const score = (t: (typeof enriched)[number]) => t.permit.expired ? 0 : !t.iot.online && t.permit.expiringSoon ? 1 : t.permit.expiringSoon ? 2 : !t.iot.online ? 3 : 4;
      const diff = score(a) - score(b);
      if (diff !== 0) return diff;
      return a.permit.days - b.permit.days;
    });
  }, [enriched, filter, search]);
  const stats = useMemo(() => {
    return {
      total: enriched.length,
      healthy: enriched.filter(t => !t.permit.expired && !t.permit.expiringSoon && t.iot.online).length,
      expiringSoon: enriched.filter(t => t.permit.expiringSoon).length,
      expired: enriched.filter(t => t.permit.expired).length,
      offline: enriched.filter(t => !t.iot.online).length
    };
  }, [enriched]);
  const payingTruck = enriched.find(t => t.id === payingTruckId) || null;
  return <SupplierLayout data-id="element-3985">
      <div className="max-w-6xl mx-auto" data-id="element-3986">
        {/* Hero header */}
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-gray-900 via-gray-900 to-[#0a5e34] text-white p-6 sm:p-8 mb-6" data-id="element-3987">
          <div className="absolute -top-24 -right-16 w-72 h-72 rounded-full bg-emerald-500/20 blur-3xl pointer-events-none" data-id="element-3988" />
          <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-brand-500/20 blur-3xl pointer-events-none" data-id="element-3989" />
          <div className="relative" data-id="element-3990">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/10 backdrop-blur-sm ring-1 ring-white/15 text-[10px] font-bold uppercase tracking-[0.18em]" data-id="element-3991">
              <ShieldCheck className="w-3 h-3" data-id="element-3992" />
              RTA Vehicle Permits
            </div>
            <h1 className="text-3xl sm:text-4xl font-display font-black mt-3 tracking-tight" data-id="element-3993">
              Permit Compliance Centre
            </h1>
            <p className="text-sm text-gray-300 mt-2 max-w-xl leading-relaxed" data-id="element-3994">
              One place to monitor RTA permits, GPS connectivity and renewal
              status for every vehicle in your fleet. Pay online in seconds.
            </p>
          </div>
        </div>

        {/* Stats strip */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6" data-id="element-3995">
          <StatTile label="Total fleet" value={stats.total} icon={TruckIcon} tone="neutral" data-id="element-3996" />
          <StatTile label="Compliant" value={stats.healthy} icon={CheckCircle2} tone="emerald" data-id="element-3997" />
          <StatTile label="Expiring ≤30d" value={stats.expiringSoon} icon={Clock} tone="amber" attention={stats.expiringSoon > 0} data-id="element-3998" />
          <StatTile label="Expired" value={stats.expired} icon={XCircle} tone="rose" attention={stats.expired > 0} data-id="element-3999" />
          <StatTile label="GPS offline" value={stats.offline} icon={WifiOff} tone="slate" attention={stats.offline > 0} data-id="element-4000" />
        </div>

        {/* Search + filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-5" data-id="element-4001">
          <div className="relative flex-1" data-id="element-4002">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" data-id="element-4003" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by plate, vehicle type or permit number…" className="w-full pl-9 pr-3 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:ring-gray-900 focus:border-gray-900 placeholder:text-gray-400" data-id="element-4004" />
          </div>
          <div className="flex items-center gap-1.5 bg-white border border-gray-200 rounded-xl p-1 overflow-x-auto scrollbar-hide" data-id="element-4005">
            {([{
            id: 'all',
            label: 'All',
            count: stats.total
          }, {
            id: 'expired',
            label: 'Expired',
            count: stats.expired
          }, {
            id: 'expiring',
            label: 'Expiring',
            count: stats.expiringSoon
          }, {
            id: 'offline',
            label: 'GPS offline',
            count: stats.offline
          }, {
            id: 'healthy',
            label: 'Compliant',
            count: stats.healthy
          }] as Array<{
            id: Filter;
            label: string;
            count: number;
          }>).map(f => {
            const active = filter === f.id;
            return <button key={f.id} onClick={() => setFilter(f.id)} className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors whitespace-nowrap flex items-center gap-1.5 ${active ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'}`} data-id="element-4006">
                  {f.label}
                  {f.count > 0 && <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${active ? 'bg-white/15 text-white' : 'bg-gray-100 text-gray-500'}`} data-id="element-4007">
                      {f.count}
                    </span>}
                </button>;
          })}
          </div>
        </div>

        {/* List */}
        {filtered.length === 0 ? <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center" data-id="element-4008">
            <FileText className="w-9 h-9 text-gray-300 mx-auto mb-2" data-id="element-4009" />
            <p className="text-sm font-bold text-gray-900" data-id="element-4010">
              No vehicles match this view
            </p>
            <p className="text-xs text-gray-500 mt-1" data-id="element-4011">
              Try a different filter or clear your search.
            </p>
          </div> : <div className="space-y-2.5" data-id="element-4012">
            {filtered.map((t, i) => <PermitRow key={t.id} truck={t} index={i} onPay={() => setPayingTruckId(t.id)} data-id="element-4013" />)}
          </div>}
      </div>

      {/* Pay-online drawer */}
      <AnimatePresence data-id="element-4014">
        {payingTruck && <PayPermitModal truck={payingTruck} onClose={() => setPayingTruckId(null)} data-id="element-4015" />}
      </AnimatePresence>
    </SupplierLayout>;
}
function StatTile({
  label,
  value,
  icon: Icon,
  tone,
  attention
}: {
  label: string;
  value: number;
  icon: typeof CheckCircle2;
  tone: 'neutral' | 'emerald' | 'amber' | 'rose' | 'slate';
  attention?: boolean;
}) {
  const toneMap: Record<string, {
    bg: string;
    text: string;
    ring: string;
  }> = {
    neutral: {
      bg: 'bg-gray-50',
      text: 'text-gray-700',
      ring: 'ring-gray-100'
    },
    emerald: {
      bg: 'bg-emerald-50',
      text: 'text-emerald-700',
      ring: 'ring-emerald-100'
    },
    amber: {
      bg: 'bg-amber-50',
      text: 'text-amber-700',
      ring: 'ring-amber-100'
    },
    rose: {
      bg: 'bg-rose-50',
      text: 'text-rose-700',
      ring: 'ring-rose-100'
    },
    slate: {
      bg: 'bg-slate-50',
      text: 'text-slate-700',
      ring: 'ring-slate-100'
    }
  };
  const t = toneMap[tone];
  return <div className={`relative bg-white border border-gray-100 rounded-2xl p-4 shadow-sm ${attention ? 'ring-1 ' + t.ring : ''}`} data-id="element-4016">
      <div className={`w-8 h-8 rounded-lg ${t.bg} ${t.text} flex items-center justify-center mb-2`} data-id="element-4017">
        <Icon className="w-4 h-4" data-id="element-4018" />
      </div>
      <p className="text-2xl font-display font-black text-gray-900 leading-none" data-id="element-4019">
        {value}
      </p>
      <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mt-1" data-id="element-4020">
        {label}
      </p>
    </div>;
}
function PermitRow({
  truck,
  index,
  onPay
}: {
  truck: ReturnType<typeof Array.prototype.map<any> extends never ? never : any> extends never ? never : any;
  index: number;
  onPay: () => void;
}) {
  const {
    permit,
    iot
  } = truck;
  // Status pill content
  let statusPill: {
    icon: typeof CheckCircle2;
    label: string;
    bg: string;
    text: string;
    ring: string;
  };
  if (permit.expired) {
    statusPill = {
      icon: XCircle,
      label: `Expired · ${Math.abs(permit.days)}d ago`,
      bg: 'bg-rose-50',
      text: 'text-rose-700',
      ring: 'ring-rose-200'
    };
  } else if (permit.expiringSoon) {
    statusPill = {
      icon: AlertTriangle,
      label: `Expires in ${permit.days}d`,
      bg: 'bg-amber-50',
      text: 'text-amber-700',
      ring: 'ring-amber-200'
    };
  } else {
    statusPill = {
      icon: CheckCircle2,
      label: `Valid · ${permit.days}d left`,
      bg: 'bg-emerald-50',
      text: 'text-emerald-700',
      ring: 'ring-emerald-200'
    };
  }
  const StatusIcon = statusPill.icon;
  const accentBar = permit.expired ? 'bg-rose-500' : permit.expiringSoon ? 'bg-amber-400' : !iot.online ? 'bg-slate-400' : 'bg-emerald-500';
  return <motion.div initial={{
    opacity: 0,
    y: 8
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    delay: Math.min(index * 0.03, 0.25)
  }} className="group relative bg-white border border-gray-100 rounded-2xl shadow-sm hover:shadow-md hover:border-gray-200 transition-all overflow-hidden" data-id="element-4021">
      {/* Accent strip on the left */}
      <span className={`absolute left-0 top-0 bottom-0 w-1 ${accentBar}`} data-id="element-4022" />

      <div className="p-4 sm:p-5 pl-5 sm:pl-6 grid grid-cols-1 lg:grid-cols-[1.5fr_1.2fr_1.2fr_160px] gap-4 lg:items-center" data-id="element-4023">
        {/* Vehicle identity */}
        <div className="flex items-center gap-3 min-w-0" data-id="element-4024">
          <div className="w-11 h-11 rounded-xl bg-gray-50 ring-1 ring-gray-100 flex items-center justify-center shrink-0" data-id="element-4025">
            <TruckIcon className="w-5 h-5 text-gray-700" data-id="element-4026" />
          </div>
          <div className="min-w-0" data-id="element-4027">
            <div className="flex items-center gap-2 flex-wrap" data-id="element-4028">
              <p className="text-base font-display font-black text-gray-900 tracking-tight" data-id="element-4029">
                {truck.plate}
              </p>
              <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400" data-id="element-4030">
                {truck.type}
              </span>
            </div>
            <p className="text-[11px] text-gray-500 mt-0.5 truncate" data-id="element-4031">
              Permit · {truck.permit.number}
            </p>
          </div>
        </div>

        {/* Permit validity */}
        <div className="flex flex-col gap-1" data-id="element-4032">
          <span className={`self-start inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full ${statusPill.bg} ${statusPill.text} ring-1 ${statusPill.ring}`} data-id="element-4033">
            <StatusIcon className="w-3 h-3" data-id="element-4034" />
            {statusPill.label}
          </span>
          <p className="text-[11px] text-gray-500 flex items-center gap-1" data-id="element-4035">
            <Calendar className="w-3 h-3" data-id="element-4036" />
            Valid until {formatDate(permit.validUntil)}
          </p>
        </div>

        {/* IoT status */}
        <div className="flex flex-col gap-1" data-id="element-4037">
          {iot.online ? <span className="self-start inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200" data-id="element-4038">
              <span className="relative flex items-center justify-center w-1.5 h-1.5" data-id="element-4039">
                <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" data-id="element-4040" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500" data-id="element-4041" />
              </span>
              GPS Online
            </span> : <span className="self-start inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-slate-100 text-slate-700 ring-1 ring-slate-200" data-id="element-4042">
              <WifiOff className="w-3 h-3" data-id="element-4043" />
              GPS Offline
            </span>}
          <p className="text-[11px] text-gray-500 flex items-center gap-1" data-id="element-4044">
            <Cpu className="w-3 h-3" data-id="element-4045" />
            {truck.gps?.brand || 'Teltonika'} · {iot.lastPing}
          </p>
        </div>

        {/* Action */}
        <div className="flex items-center gap-2 lg:justify-end" data-id="element-4046">
          {!iot.online ? <button type="button" onClick={() => toast.error('GPS offline — bring the tracker online first', {
          description: 'RTA requires a live GPS signal before permit payment can be processed. Check the device and try again.',
          duration: 5000
        })} title="GPS offline — bring the tracker online first to renew" className="inline-flex items-center justify-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-bold bg-slate-100 hover:bg-slate-200 text-slate-700 ring-1 ring-slate-200 transition-colors w-full" data-id="element-4047">
              <WifiOff className="w-3.5 h-3.5" data-id="element-4048" />
              Check GPS first
            </button> : permit.expired || permit.expiringSoon ? <button onClick={onPay} className={`inline-flex items-center justify-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-bold transition-colors w-full ${permit.expired ? 'bg-rose-600 hover:bg-rose-700 text-white' : 'bg-gray-900 hover:bg-black text-white'}`} data-id="element-4049">
              <CreditCard className="w-3.5 h-3.5" data-id="element-4050" />
              Pay {permit.expired ? 'overdue' : 'renewal'}
            </button> : <button type="button" disabled aria-disabled="true" title="No action needed — permit is valid" className="inline-flex items-center justify-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-bold bg-gray-50 text-gray-400 ring-1 ring-gray-100 cursor-not-allowed w-full" data-id="element-4051">
              <CheckCircle2 className="w-3.5 h-3.5" data-id="element-4052" />
              No action needed
            </button>}
        </div>
      </div>
    </motion.div>;
}
function PayPermitModal({
  truck,
  onClose
}: {
  truck: any;
  onClose: () => void;
}) {
  const [paying, setPaying] = useState(false);
  const [done, setDone] = useState(false);
  const fee = truck.permit.expired ? 850 : 620; // AED — overdue carries a penalty
  const handlePay = () => {
    setPaying(true);
    setTimeout(() => {
      setPaying(false);
      setDone(true);
      toast.success(`Permit renewed for ${truck.plate}`);
      setTimeout(onClose, 1400);
    }, 1100);
  };
  const newValidDate = new Date();
  newValidDate.setFullYear(newValidDate.getFullYear() + 1);
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.18
  }} className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-4053">
      <div onClick={paying || done ? undefined : onClose} className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm" data-id="element-4054" />
      <motion.div initial={{
      y: 30,
      opacity: 0
    }} animate={{
      y: 0,
      opacity: 1
    }} exit={{
      y: 30,
      opacity: 0
    }} transition={{
      type: 'spring',
      stiffness: 320,
      damping: 32
    }} className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden" data-id="element-4055">
        {/* RTA-toned header */}
        <div className="relative overflow-hidden bg-gradient-to-br from-[#0a5e34] to-[#0d6b3a] text-white p-6" data-id="element-4056">
          <div className="absolute -top-12 -right-12 w-44 h-44 rounded-full bg-white/10 blur-2xl pointer-events-none" data-id="element-4057" />
          <div className="relative flex items-start justify-between" data-id="element-4058">
            <div data-id="element-4059">
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/15 ring-1 ring-white/20 text-[10px] font-bold uppercase tracking-[0.18em]" data-id="element-4060">
                <ShieldCheck className="w-3 h-3" data-id="element-4061" />
                RTA Permit Renewal
              </div>
              <h2 className="text-xl font-display font-black mt-3 tracking-tight" data-id="element-4062">
                {truck.plate}
              </h2>
              <p className="text-xs text-emerald-100 mt-0.5" data-id="element-4063">
                {truck.type} · {truck.permit.number}
              </p>
            </div>
            {!paying && !done && <button onClick={onClose} className="p-1.5 rounded-full hover:bg-white/10 transition-colors" aria-label="Close" data-id="element-4064">
                <X className="w-4 h-4" data-id="element-4065" />
              </button>}
          </div>
        </div>

        <div className="p-6" data-id="element-4066">
          {done ? <motion.div initial={{
          scale: 0.9,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} className="text-center py-4" data-id="element-4067">
              <div className="w-14 h-14 rounded-2xl bg-emerald-50 ring-1 ring-emerald-100 mx-auto flex items-center justify-center mb-3" data-id="element-4068">
                <CheckCircle2 className="w-7 h-7 text-emerald-600" data-id="element-4069" />
              </div>
              <h3 className="font-display font-black text-lg text-gray-900" data-id="element-4070">
                Permit renewed
              </h3>
              <p className="text-xs text-gray-500 mt-1" data-id="element-4071">
                Valid until {formatDate(newValidDate)}
              </p>
            </motion.div> : <>
              <div className="space-y-2.5 mb-5" data-id="element-4072">
                <PayRow label="Permit fee" value="AED 600" data-id="element-4073" />
                {truck.permit.expired && <PayRow label="Overdue penalty" value="AED 250" tone="rose" data-id="element-4074" />}
                <PayRow label="Service charge" value="AED 20" data-id="element-4075" />
                <PayRow label="VAT (5%)" value="AED 30" data-id="element-4076" />
                <div className="h-px bg-gray-100 my-2" data-id="element-4077" />
                <PayRow label="Total" value={`AED ${fee}`} bold data-id="element-4078" />
              </div>
              <div className="bg-gray-50 border border-gray-100 rounded-xl p-3 mb-5 flex items-start gap-2" data-id="element-4079">
                <Sparkles className="w-3.5 h-3.5 text-brand-500 mt-0.5 shrink-0" data-id="element-4080" />
                <p className="text-[11px] text-gray-600 leading-relaxed" data-id="element-4081">
                  Renewing extends validity by 12 months to{' '}
                  <span className="font-bold text-gray-900" data-id="element-4082">
                    {formatDate(newValidDate)}
                  </span>
                  .
                </p>
              </div>
              <button onClick={handlePay} disabled={paying} className="w-full py-3.5 rounded-2xl bg-gray-900 hover:bg-black text-white font-bold text-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-60" data-id="element-4083">
                {paying ? <>
                    <motion.div animate={{
                rotate: 360
              }} transition={{
                repeat: Infinity,
                duration: 0.8,
                ease: 'linear'
              }} className="w-4 h-4 border-2 border-white border-t-transparent rounded-full" data-id="element-4084" />
                    Processing payment…
                  </> : <>
                    <Lock className="w-4 h-4" data-id="element-4085" />
                    Pay AED {fee}
                  </>}
              </button>
              <p className="text-[10px] text-gray-400 text-center mt-3 leading-snug" data-id="element-4086">
                Secure transaction · Charged directly to your registered RTA
                business account.
              </p>
            </>}
        </div>
      </motion.div>
    </motion.div>;
}
function PayRow({
  label,
  value,
  bold,
  tone
}: {
  label: string;
  value: string;
  bold?: boolean;
  tone?: 'rose';
}) {
  return <div className="flex items-center justify-between text-sm" data-id="element-4087">
      <span className={`${tone === 'rose' ? 'text-rose-600' : 'text-gray-600'} ${bold ? 'font-bold text-gray-900' : ''}`} data-id="element-4088">
        {label}
      </span>
      <span className={`${tone === 'rose' ? 'text-rose-600' : 'text-gray-900'} ${bold ? 'font-display font-black text-base' : 'font-semibold'}`} data-id="element-4089">
        {value}
      </span>
    </div>;
}